import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  getGSTR1,
  getGSTR3B,
  downloadGSTR1CSV,
  downloadGSTR1PDF,
  downloadGSTR3BCSV,
  downloadGSTR3BPDF,
  getGSTDashboard,
  getGSTInvoices,
  getGSTInvoiceById,
  generateGSTInvoices,
  cancelGSTInvoice,
  getGSTRates,
} from '../../Services/Operations/ProductAPI';
import '../../Styles/GSTAdmin.css';

const SkeletonSummaryCard = () => (
  <div className="summary-card skeleton">
    <div className="card-header">
      <div>
        <div className="skeleton-title"></div>
        <div className="skeleton-value"></div>
        <div className="skeleton-change"></div>
      </div>
      <div className="skeleton-icon"></div>
    </div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-status"></div></td>
    <td><div className="skeleton-action"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>Invoice No.</th>
          <th>PNR</th>
          <th>Date</th>
          <th>Route</th>
          <th>Taxable Value</th>
          <th>GST Rate</th>
          <th>CGST</th>
          <th>SGST</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const GSTAdminPanel = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  
  const [dashboardData, setDashboardData] = useState(null);
  const [gstr1Data, setGstr1Data] = useState(null);
  const [gstr3bData, setGstr3bData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [gstRates, setGstRates] = useState([]);
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const [cancellingInvoice, setCancellingInvoice] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [error, setError] = useState(null);
  
  const [filters, setFilters] = useState({
    status: '',
    month: '',
    year: '',
    busOwner: ''
  });

  const { token } = useSelector(state => state.user);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
  };

  const fetchDashboardData = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await getGSTDashboard(selectedYear, token);
      if (response.success) {
        setDashboardData(response.data);
        setError(null);
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      setError(error.response?.data?.message || 'Failed to load dashboard data');
      showNotification('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedYear, token]);

  const fetchGSTR1Data = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await getGSTR1(selectedMonth, selectedYear, null, token);
      if (response.success) {
        setGstr1Data(response.data);
        setError(null);
      } else {
        setError('Failed to load GSTR-1 data');
      }
    } catch (error) {
      console.error('Error fetching GSTR-1:', error);
      setError(error.response?.data?.message || 'Failed to load GSTR-1 data');
      showNotification('Failed to load GSTR-1 data', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, token]);

  const fetchGSTR3BData = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await getGSTR3B(selectedMonth, selectedYear, null, token);
      if (response.success) {
        setGstr3bData(response.data);
        setError(null);
      } else {
        setError('Failed to load GSTR-3B data');
      }
    } catch (error) {
      console.error('Error fetching GSTR-3B:', error);
      setError(error.response?.data?.message || 'Failed to load GSTR-3B data');
      showNotification('Failed to load GSTR-3B data', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear, token]);

  const fetchInvoices = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await getGSTInvoices(currentPage, itemsPerPage, filters, token);
      if (response.success) {
        setInvoices(response.data.invoices);
        setPagination(response.data.pagination);
        setError(null);
      } else {
        setError('Failed to load invoices');
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setError(error.response?.data?.message || 'Failed to load invoices');
      showNotification('Failed to load invoices', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, filters, token]);

  const fetchGSTRates = useCallback(async () => {
    if (!token) return;
    try {
      const response = await getGSTRates({}, token);
      if (response.success) {
        setGstRates(response.data);
      }
    } catch (error) {
      console.error('Error fetching GST rates:', error);
    }
  }, [token]);

  const handleGenerateInvoices = async () => {
    if (!token) {
      showNotification('Please login first', 'error');
      return;
    }
    setGeneratingInvoices(true);
    try {
      const response = await generateGSTInvoices(selectedMonth, selectedYear, token);
      if (response.success) {
        showNotification(response.message, 'success');
        fetchInvoices();
        fetchDashboardData();
      } else {
        showNotification(response.message || 'Failed to generate invoices', 'error');
      }
    } catch (error) {
      console.error('Error generating invoices:', error);
      showNotification(error.response?.data?.message || 'Failed to generate invoices', 'error');
    } finally {
      setGeneratingInvoices(false);
    }
  };

  const handleCancelInvoice = async (invoiceId, reason) => {
    if (!token) {
      showNotification('Please login first', 'error');
      return;
    }
    setCancellingInvoice(true);
    try {
      const response = await cancelGSTInvoice(invoiceId, reason, token);
      if (response.success) {
        showNotification('Invoice cancelled and credit note created', 'success');
        fetchInvoices();
        fetchDashboardData();
        setShowInvoiceModal(false);
      } else {
        showNotification(response.message || 'Failed to cancel invoice', 'error');
      }
    } catch (error) {
      console.error('Error cancelling invoice:', error);
      showNotification(error.response?.data?.message || 'Failed to cancel invoice', 'error');
    } finally {
      setCancellingInvoice(false);
    }
  };

  const handleViewInvoice = async (invoiceId) => {
    if (!token) {
      showNotification('Please login first', 'error');
      return;
    }
    try {
      setLoading(true);
      const response = await getGSTInvoiceById(invoiceId, token);
      if (response.success) {
        setSelectedInvoice(response.data);
        setShowInvoiceModal(true);
      } else {
        showNotification('Failed to load invoice details', 'error');
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      showNotification('Failed to load invoice details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadGSTR1CSV = async () => {
    if (!token) {
      showNotification('Please login first', 'error');
      return;
    }
    try {
      const blob = await downloadGSTR1CSV(selectedMonth, selectedYear, null, token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GSTR1_${selectedMonth}_${selectedYear}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showNotification('GSTR-1 CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading GSTR-1 CSV:', error);
      showNotification('Failed to download GSTR-1 CSV', 'error');
    }
  };

  const handleDownloadGSTR1PDF = async () => {
    if (!token) {
      showNotification('Please login first', 'error');
      return;
    }
    try {
      const blob = await downloadGSTR1PDF(selectedMonth, selectedYear, null, token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GSTR1_${selectedMonth}_${selectedYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showNotification('GSTR-1 PDF downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading GSTR-1 PDF:', error);
      showNotification('Failed to download GSTR-1 PDF', 'error');
    }
  };

  const handleDownloadGSTR3BCSV = async () => {
    if (!token) {
      showNotification('Please login first', 'error');
      return;
    }
    try {
      const blob = await downloadGSTR3BCSV(selectedMonth, selectedYear, null, token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GSTR3B_${selectedMonth}_${selectedYear}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showNotification('GSTR-3B CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading GSTR-3B CSV:', error);
      showNotification('Failed to download GSTR-3B CSV', 'error');
    }
  };

  const handleDownloadGSTR3BPDF = async () => {
    if (!token) {
      showNotification('Please login first', 'error');
      return;
    }
    try {
      const blob = await downloadGSTR3BPDF(selectedMonth, selectedYear, null, token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GSTR3B_${selectedMonth}_${selectedYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showNotification('GSTR-3B PDF downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading GSTR-3B PDF:', error);
      showNotification('Failed to download GSTR-3B PDF', 'error');
    }
  };

  const handleRefresh = () => {
    if (activeTab === 'dashboard') {
      fetchDashboardData();
    } else if (activeTab === 'gstr1') {
      fetchGSTR1Data();
    } else if (activeTab === 'gstr3b') {
      fetchGSTR3BData();
    } else if (activeTab === 'invoices') {
      setCurrentPage(1);
      fetchInvoices();
    }
  };

  useEffect(() => {
    if (token) {
      fetchDashboardData();
      fetchGSTRates();
    }
  }, [fetchDashboardData, fetchGSTRates, token]);

  useEffect(() => {
    if (token) {
      if (activeTab === 'gstr1') {
        fetchGSTR1Data();
      } else if (activeTab === 'gstr3b') {
        fetchGSTR3BData();
      } else if (activeTab === 'invoices') {
        fetchInvoices();
      }
    }
  }, [activeTab, fetchGSTR1Data, fetchGSTR3BData, fetchInvoices, token]);

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0';
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1];
  };

  const getSummaryCards = () => {
    if (!dashboardData) return [];
    return [
      { title: 'Total Invoices', value: dashboardData.currentMonth?.count || 0, change: '+0%', icon: '📄', color: 'blue' },
      { title: 'Total Taxable Value', value: formatCurrency(dashboardData.currentMonth?.totalTaxable || 0), change: '+0%', icon: '💰', color: 'green' },
      { title: 'Total GST Collected', value: formatCurrency(dashboardData.currentMonth?.totalGST || 0), change: '+0%', icon: '🏷️', color: 'purple' },
      { title: 'Pending Invoices', value: dashboardData.pendingInvoiceGeneration || 0, change: '⚠️', icon: '⏳', color: 'orange' },
    ];
  };

  if (loading && !dashboardData && activeTab === 'dashboard') {
    return (
      <div className="gst-admin-container">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>
        <div className="summary-grid">
          {[1, 2, 3, 4].map((i) => <SkeletonSummaryCard key={i} />)}
        </div>
        <div className="skeleton-table"></div>
      </div>
    );
  }

  return (
    <div className="gst-admin-container">

      {notification.show && (
        <div className={`notification notification-${notification.type}`}>
          <span>{notification.message}</span>
          <button onClick={() => setNotification({ show: false, message: '', type: '' })}>×</button>
        </div>
      )}


      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}


      <header className="gst-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-icon">
              <span>🚌</span>
            </div>
            <div>
              <h1 className="header-title">Roam - GST Compliance Hub</h1>
              <p className="header-subtitle">Manage bus transport GSTR-1 & GSTR-3B returns</p>
            </div>
          </div>
          <div className="header-actions">
            <div className="period-selector">
              <span className="calendar-icon">📅</span>
              <select 
                className="period-dropdown"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                  <option key={m} value={m}>{getMonthName(m)}</option>
                ))}
              </select>
              <select 
                className="period-dropdown"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              >
                {[2024, 2025, 2026, 2027].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button className="btn-primary" onClick={handleGenerateInvoices} disabled={generatingInvoices}>
              {generatingInvoices ? 'Generating...' : '+ Generate Invoices'}
            </button>
            <button className="refresh-btn" onClick={handleRefresh} disabled={loading}>
              🔄 Refresh
            </button>
          </div>
        </div>
      </header>

      <div className="main-content">

        <div className="summary-grid">
          {getSummaryCards().map((card, idx) => (
            <div key={idx} className={`summary-card summary-card-${card.color}`}>
              <div className="card-header">
                <div>
                  <p className="card-title">{card.title}</p>
                  <h3 className="card-value">{card.value}</h3>
                  <p className={`card-change ${card.change.startsWith('+') ? 'change-positive' : 'change-negative'}`}>
                    {card.change} from last month
                  </p>
                </div>
                <div className={`card-icon card-icon-${card.color}`}>
                  <span>{card.icon}</span>
                </div>
              </div>
            </div>
          ))}
        </div>


        <div className="tabs-container">
          <div className="tabs-header">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`tab-button ${activeTab === 'dashboard' ? 'tab-active' : ''}`}
            >
              <span className="tab-icon">📊</span>
              <span>Dashboard</span>
            </button>
            <button
              onClick={() => setActiveTab('gstr1')}
              className={`tab-button ${activeTab === 'gstr1' ? 'tab-active' : ''}`}
            >
              <span className="tab-icon">📋</span>
              <span>GSTR-1</span>
            </button>
            <button
              onClick={() => setActiveTab('gstr3b')}
              className={`tab-button ${activeTab === 'gstr3b' ? 'tab-active' : ''}`}
            >
              <span className="tab-icon">📊</span>
              <span>GSTR-3B</span>
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              className={`tab-button ${activeTab === 'invoices' ? 'tab-active' : ''}`}
            >
              <span className="tab-icon">📄</span>
              <span>Invoices</span>
            </button>
            <button
              onClick={() => setActiveTab('rates')}
              className={`tab-button ${activeTab === 'rates' ? 'tab-active' : ''}`}
            >
              <span className="tab-icon">⚙️</span>
              <span>GST Rates</span>
            </button>
          </div>

          <div className="tab-content">

            {activeTab === 'dashboard' && dashboardData && (
              <div className="dashboard-content">
                {loading && <div className="loading-overlay"><div className="loading-spinner"></div></div>}

                <div className="trend-chart">
                  <h3>Monthly GST Collection Trend</h3>
                  <div className="chart-bars">
                    {dashboardData.financialYearMonthly?.map((item, idx) => (
                      <div key={idx} className="chart-bar-container">
                        <div 
                          className="chart-bar" 
                          style={{ 
                            height: `${Math.min((item.totalGST / 10000) * 100, 100)}%`,
                            backgroundColor: '#4F46E5'
                          }}
                        ></div>
                        <span className="chart-label">{getMonthName(item._id.month).slice(0, 3)}</span>
                        <span className="chart-value">{formatCurrency(item.totalGST)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="financial-summary">
                  <h3>Financial Year {dashboardData.financialYear} Summary</h3>
                  <div className="summary-stats-grid">
                    <div className="stat-item">
                      <span className="stat-label">Total Invoices</span>
                      <span className="stat-value">{dashboardData.financialYearMonthly?.reduce((sum, i) => sum + i.count, 0) || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Total GST Collected</span>
                      <span className="stat-value">{formatCurrency(dashboardData.financialYearMonthly?.reduce((sum, i) => sum + i.totalGST, 0) || 0)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Total Invoice Value</span>
                      <span className="stat-value">{formatCurrency(dashboardData.financialYearMonthly?.reduce((sum, i) => sum + i.totalValue, 0) || 0)}</span>
                    </div>
                  </div>
                </div>

                {dashboardData.pendingInvoiceGeneration > 0 && (
                  <div className="pending-alert">
                    <div className="alert-icon">⚠️</div>
                    <div className="alert-content">
                      <h4>{dashboardData.pendingInvoiceGeneration} bookings pending invoice generation</h4>
                      <p>Click "Generate Invoices" to create GST invoices for these bookings</p>
                    </div>
                    <button className="alert-action" onClick={handleGenerateInvoices}>
                      Generate Now
                    </button>
                  </div>
                )}
              </div>
            )}


            {activeTab === 'gstr1' && gstr1Data && (
              <div className="gstr1-content">
                {loading && <div className="loading-overlay"><div className="loading-spinner"></div></div>}
                <div className="action-bar">
                  <div className="action-buttons">
                    <button className="btn-secondary" onClick={handleDownloadGSTR1CSV}>
                      <span>📥</span>
                      <span>Download CSV</span>
                    </button>
                    <button className="btn-secondary" onClick={handleDownloadGSTR1PDF}>
                      <span>📄</span>
                      <span>Download PDF</span>
                    </button>
                  </div>
                </div>

                <div className="summary-section">
                  <h3>GSTR-1 Summary - {getMonthName(selectedMonth)} {selectedYear}</h3>
                  <div className="summary-grid-small">
                    <div className="summary-item">
                      <span className="summary-label">Total Invoices</span>
                      <span className="summary-value">{gstr1Data.summary?.totalInvoices || 0}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total Taxable Value</span>
                      <span className="summary-value">{formatCurrency(gstr1Data.summary?.totalTaxableValue || 0)}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total CGST</span>
                      <span className="summary-value">{formatCurrency(gstr1Data.summary?.totalCGST || 0)}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total SGST</span>
                      <span className="summary-value">{formatCurrency(gstr1Data.summary?.totalSGST || 0)}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total IGST</span>
                      <span className="summary-value">{formatCurrency(gstr1Data.summary?.totalIGST || 0)}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Total GST</span>
                      <span className="summary-value">{formatCurrency(gstr1Data.summary?.totalGST || 0)}</span>
                    </div>
                  </div>
                </div>


              </div>
            )}


            {activeTab === 'gstr3b' && gstr3bData && (
              <div className="gstr3b-content">
                {loading && <div className="loading-overlay"><div className="loading-spinner"></div></div>}

              </div>
            )}


            {activeTab === 'invoices' && (
              <div className="invoices-content">
                <div className="filters-bar">
                  <div className="search-box">
                    <span className="search-icon">🔍</span>
                    <input
                      type="text"
                      placeholder="Search by invoice number or PNR..."
                      className="search-input"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <select 
                    className="filter-select"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="credit_note">Credit Note</option>
                  </select>
                  <button className="btn-refresh" onClick={handleRefresh}>
                    🔄 Refresh
                  </button>
                </div>


                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Invoice No.</th>
                        <th>PNR</th>
                        <th>Date</th>
                        <th>Route</th>
                        <th className="text-right">Taxable Value</th>
                        <th className="text-right">CGST</th>
                        <th className="text-right">SGST</th>
                        <th className="text-right">Total</th>
                        <th className="text-center">Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && invoices.length === 0 ? (
                        [...Array(5)].map((_, i) => <SkeletonTableRow key={i} />)
                      ) : invoices.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="no-data">No invoices found</td>
                        </tr>
                      ) : (
                        invoices.map((invoice) => (
                          <tr key={invoice._id}>
                            <td>{invoice.invoiceNumber}</td>
                            <td>{invoice.pnr}</td>
                            <td>{formatDate(invoice.invoiceDate)}</td>
                            <td>
                              {invoice.booking?.route?.from} → {invoice.booking?.route?.to}
                            </td>
                            <td className="text-right">{formatCurrency(invoice.totalTaxableValue)}</td>
                            <td className="text-right">{formatCurrency(invoice.totalCGST)}</td>
                            <td className="text-right">{formatCurrency(invoice.totalSGST)}</td>
                            <td className="text-right">{formatCurrency(invoice.totalInvoiceValue)}</td>
                            <td className="text-center">
                              <span className={`status-badge status-${invoice.status}`}>
                                {invoice.status === 'active' ? 'Active' : invoice.status === 'cancelled' ? 'Cancelled' : 'Credit Note'}
                              </span>
                            </td>
                            <td className="text-center">
                              <button 
                                className="action-btn view" 
                                onClick={() => handleViewInvoice(invoice._id)}
                                title="View"
                              >
                                👁️
                              </button>
                              {invoice.status === 'active' && (
                                <button 
                                  className="action-btn cancel" 
                                  onClick={() => {
                                    const reason = prompt('Enter cancellation reason:');
                                    if (reason) handleCancelInvoice(invoice._id, reason);
                                  }}
                                  title="Cancel"
                                >
                                  ❌
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>


                {pagination.pages > 1 && (
                  <div className="pagination-container">
                    <div className="pagination-info">
                      Showing {invoices.length} of {pagination.total} invoices
                      (Page {currentPage} of {pagination.pages})
                    </div>
                    <div className="pagination-controls">
                      <button 
                        className="pagination-btn"
                        disabled={currentPage === 1 || loading}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        Previous
                      </button>
                      <span className="page-number active">{currentPage}</span>
                      <button 
                        className="pagination-btn"
                        disabled={currentPage === pagination.pages || loading}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}


            {activeTab === 'rates' && (
              <div className="rates-content">
                <div className="table-section">
                  <h3>GST Rates Configuration</h3>
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Service Type</th>
                          <th>Tax Type</th>
                          <th className="text-right">GST Rate (%)</th>
                          <th className="text-right">CGST (%)</th>
                          <th className="text-right">SGST (%)</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>AC Bus</td>
                          <td>Intra-State</td>
                          <td className="text-right">5%</td>
                          <td className="text-right">2.5%</td>
                          <td className="text-right">2.5%</td>
                          <td><span className="status-badge status-active">Active</span></td>
                        </tr>
                        <tr>
                          <td>AC Bus</td>
                          <td>Inter-State</td>
                          <td className="text-right">5%</td>
                          <td className="text-right">-</td>
                          <td className="text-right">-</td>
                          <td><span className="status-badge status-active">Active</span></td>
                        </tr>
                        <tr>
                          <td>Non-AC Bus</td>
                          <td>All</td>
                          <td className="text-right">0%</td>
                          <td className="text-right">0%</td>
                          <td className="text-right">0%</td>
                          <td><span className="status-badge status-exempt">Exempt</span></td>
                        </tr>
                        <tr>
                          <td>Luxury Coach</td>
                          <td>Intra-State</td>
                          <td className="text-right">12%</td>
                          <td className="text-right">6%</td>
                          <td className="text-right">6%</td>
                          <td><span className="status-badge status-active">Active</span></td>
                        </tr>
                        <tr>
                          <td>Luxury Coach</td>
                          <td>Inter-State</td>
                          <td className="text-right">12%</td>
                          <td className="text-right">-</td>
                          <td className="text-right">-</td>
                          <td><span className="status-badge status-active">Active</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="gst-info-box">
                  <span className="info-icon">ℹ️</span>
                  <div className="info-content">
                    <strong>GST Rates Applied:</strong> AC Buses @ 5% IGST/CGST+SGST | Non-AC Buses @ 0% (Exempt) | Luxury Coaches @ 12%
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>


      {showInvoiceModal && selectedInvoice && (
        <div className="modal-overlay" onClick={() => setShowInvoiceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Invoice Details</h2>
              <button className="modal-close" onClick={() => setShowInvoiceModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="invoice-details">
                <div className="detail-row">
                  <span className="detail-label">Invoice Number:</span>
                  <span className="detail-value">{selectedInvoice.invoiceNumber}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">PNR:</span>
                  <span className="detail-value">{selectedInvoice.pnr}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Invoice Date:</span>
                  <span className="detail-value">{formatDate(selectedInvoice.invoiceDate)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Financial Year:</span>
                  <span className="detail-value">{selectedInvoice.financialYear}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Recipient:</span>
                  <span className="detail-value">{selectedInvoice.recipient?.name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Place of Supply:</span>
                  <span className="detail-value">{selectedInvoice.placeOfSupply?.stateName}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Inter-State:</span>
                  <span className="detail-value">{selectedInvoice.isInterState ? 'Yes' : 'No'}</span>
                </div>
                <hr />
                <div className="detail-row">
                  <span className="detail-label">Taxable Value:</span>
                  <span className="detail-value">{formatCurrency(selectedInvoice.totalTaxableValue)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">CGST:</span>
                  <span className="detail-value">{formatCurrency(selectedInvoice.totalCGST)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">SGST:</span>
                  <span className="detail-value">{formatCurrency(selectedInvoice.totalSGST)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Total Invoice Value:</span>
                  <span className="detail-value">{formatCurrency(selectedInvoice.totalInvoiceValue)}</span>
                </div>
                <hr />
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className={`detail-value status-${selectedInvoice.status}`}>
                    {selectedInvoice.status === 'active' ? 'Active' : selectedInvoice.status === 'cancelled' ? 'Cancelled' : 'Credit Note'}
                  </span>
                </div>
                {selectedInvoice.notes && (
                  <div className="detail-row">
                    <span className="detail-label">Notes:</span>
                    <span className="detail-value">{selectedInvoice.notes}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              {selectedInvoice.status === 'active' && (
                <button 
                  className="btn-danger"
                  onClick={() => {
                    const reason = prompt('Enter cancellation reason:');
                    if (reason) {
                      handleCancelInvoice(selectedInvoice._id, reason);
                    }
                  }}
                  disabled={cancellingInvoice}
                >
                  {cancellingInvoice ? 'Cancelling...' : 'Cancel Invoice'}
                </button>
              )}
              <button className="btn-secondary" onClick={() => setShowInvoiceModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GSTAdminPanel;